import { redactJotformText } from 'src/utils/security/jotform-privacy';
import { redactPaymentLinkText } from 'src/utils/security/redact-sensitive-data';
import {
  sanitizeBoundedValue,
  isBoundedSanitizeSensitiveKey,
} from 'src/utils/security/bounded-sanitize';

/**
 * Diagnostic sanitization — two layers.
 *
 * Structured values go through the shared bounded sanitizer, which redacts by
 * key and caps depth, breadth and total nodes. Free text then goes through a
 * pattern scrubber, because a key-based pass cannot see a token pasted into a
 * message string.
 *
 * The IPv4 pattern used by the payment debug kit is deliberately NOT applied
 * here: LAN terminal addresses are the single most useful field when a
 * BlockChyp route goes missing, and they are not a credential.
 */

export const DIAGNOSTIC_MESSAGE_LIMIT = 1_000;
export const DIAGNOSTIC_DATA_BYTE_LIMIT = 8_192;

const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const STRIPE_SECRET_PATTERN = /\b(?:pst|sk|rk)_(?:test|live)_[A-Za-z0-9_]+\b/g;
const STRIPE_CLIENT_SECRET_PATTERN = /\b(?:pi|seti)_[A-Za-z0-9]+_secret_[A-Za-z0-9_]+\b/g;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
/** Key, then the separator (quote/colon/equals), then the value to mask. */
const CREDENTIAL_ASSIGNMENT_PATTERN =
  /\b(pc|pairing[ _-]?code|api[ _-]?key|bearer[ _-]?token|signing[ _-]?key|private[ _-]?key|authorization|password|passcode|pin|secret|(?:connection|client|refresh|access|id)?[ _-]?token|client[ _-]?secret)\b(\s*["']?\s*[:=]\s*)("(?:\\.|[^"\\])*"?|'(?:\\.|[^'\\])*'?|\[redacted\]|[^\s,;&}\]"']+)/gi;
/** 13–19 digits, optionally separated, checked with Luhn before masking. */
const CANDIDATE_PAN_PATTERN =
  /\b[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}\b|\b(?:\d[ -]?){12,18}\d\b/gi;

const REDACTED = '[redacted]';

function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (digit < 0 || digit > 9) return false;
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}

function maskPans(value: string): string {
  return value.replace(CANDIDATE_PAN_PATTERN, (match) => {
    if (match.length === 36 && match.split('-').length === 5) return match;
    const digits = match.replace(/[^0-9]/g, '');
    if (digits.length < 13 || digits.length > 19) return match;
    if (!passesLuhn(digits)) return match;
    return `[pan:${digits.slice(-4)}]`;
  });
}

/** Scrubs credentials out of free text. Safe on any string, including JSON. */
export function scrubDiagnosticText(value: string): string {
  try {
    return maskPans(
      redactPaymentLinkText(redactJotformText(value, REDACTED), REDACTED)
        .replace(BEARER_PATTERN, 'Bearer [redacted]')
        .replace(STRIPE_SECRET_PATTERN, REDACTED)
        .replace(STRIPE_CLIENT_SECRET_PATTERN, REDACTED)
        .replace(JWT_PATTERN, REDACTED)
        .replace(
          CREDENTIAL_ASSIGNMENT_PATTERN,
          (_match, key: string, separator: string, secret: string) => {
            const quote = ['"', "'"].includes(secret[0]) ? secret[0] : '';
            return `${key}${separator}${quote}${REDACTED}${quote}`;
          }
        )
    );
  } catch {
    return REDACTED;
  }
}

/** Preserve the full URL and ordinary query/fragment values, masking only credentials. */
export function sanitizeDiagnosticUrl(value: string): string {
  const withoutUserInfo = value.replace(/(https?:\/\/)[^\s/@]+@/gi, '$1[redacted]@');
  const scrubbed = withoutUserInfo.replace(
    /([?&#])([^=?&#\s"'<>\\]+)=([^&#\s"'<>\\]*)/g,
    (match, separator: string, key: string, raw: string) => {
      try {
        const decodedKey = decodeURIComponent(key.replace(/\+/g, ' '));
        const decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
        const safe = isBoundedSanitizeSensitiveKey(decodedKey)
          ? REDACTED
          : scrubDiagnosticText(decoded);
        return safe === decoded ? match : `${separator}${key}=${encodeURIComponent(safe)}`;
      } catch {
        return `${separator}${key}=${REDACTED}`;
      }
    }
  );
  return scrubDiagnosticText(scrubbed);
}

/** Scrubs and truncates a log message. Always returns a usable string. */
export function sanitizeDiagnosticMessage(value: unknown): string {
  let text: string;
  try {
    text = typeof value === 'string' ? value : String(value);
  } catch {
    return '[unprintable message]';
  }
  const scrubbed = sanitizeDiagnosticUrl(text);
  return scrubbed.length > DIAGNOSTIC_MESSAGE_LIMIT
    ? `${scrubbed.slice(0, DIAGNOSTIC_MESSAGE_LIMIT - 1)}…`
    : scrubbed;
}

export function utf8ByteLength(value: string): number {
  try {
    return new TextEncoder().encode(value).byteLength;
  } catch {
    // Match TextEncoder, including replacement of lone UTF-16 surrogates.
    let bytes = 0;
    for (let index = 0; index < value.length; index += 1) {
      const code = value.charCodeAt(index);
      if (code < 0x80) bytes += 1;
      else if (code < 0x800) bytes += 2;
      else if (
        code >= 0xd800 &&
        code <= 0xdbff &&
        value.charCodeAt(index + 1) >= 0xdc00 &&
        value.charCodeAt(index + 1) <= 0xdfff
      ) {
        bytes += 4;
        index += 1;
      } else bytes += 3;
    }
    return bytes;
  }
}

/**
 * Bounds and scrubs structured data, applying text scrubbing during traversal.
 * Payloads above the byte limit degrade to a truncated preview rather than
 * being dropped, so the shape of an oversized object stays visible.
 */
export function sanitizeDiagnosticData(value: unknown): unknown {
  if (value === undefined) return undefined;
  try {
    // Scrubbing happens per string during the traversal, not over the
    // serialized form: rewriting JSON text can produce invalid JSON.
    const bounded = sanitizeBoundedValue(value, { transformString: sanitizeDiagnosticUrl });
    if (bounded === undefined) return undefined;

    const serialized = JSON.stringify(bounded);
    if (serialized === undefined) return undefined;
    if (utf8ByteLength(serialized) <= DIAGNOSTIC_DATA_BYTE_LIMIT) return bounded;

    return {
      truncated: true,
      preview: `${serialized.slice(0, DIAGNOSTIC_DATA_BYTE_LIMIT)}…`,
    };
  } catch {
    return '[unserializable]';
  }
}

export type DiagnosticSafeError = {
  name: string;
  message: string;
  stack?: string;
};

/** Normalizes anything thrown into a bounded, scrubbed shape. */
export function toDiagnosticSafeError(error: unknown): DiagnosticSafeError {
  if (error instanceof Error) {
    return {
      name: sanitizeDiagnosticMessage(error.name || 'Error'),
      message: sanitizeDiagnosticMessage(error.message),
      ...(typeof error.stack === 'string'
        ? { stack: scrubDiagnosticText(error.stack).slice(0, 2_000) }
        : {}),
    };
  }
  return { name: 'NonError', message: sanitizeDiagnosticMessage(error) };
}
